/**
 * Einstiegspunkt — Auth-Gate → Weltenauswahl → Spielstart.
 * Prüft Session → zeigt ggf. LoginScreen → Weltenauswahl → Boot.
 */
import { EventBus } from './core/EventBus';
import { createSupabaseClient } from './network/SupabaseClient';
import { AuthService } from './network/AuthService';
import { LoginScreen } from './ui/LoginScreen';
import { WorldSelectScreen } from './ui/WorldSelectScreen';
import { LoadingScreen } from './ui/LoadingScreen';
import { bootGame } from './core/GameBoot';
import type { AuthUser } from './network/AuthTypes';
import {
  getLastWorldId, getAvailableWorlds, getPlayerWorlds,
  joinWorld, getPlayerPlanets,
} from './world/PlayerWorldService';
import type { PlanetSummary } from './world/PlayerWorldTypes';

const supabase = createSupabaseClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY,
);
const authEvents = new EventBus();
const authService = new AuthService(supabase, authEvents);

/** Zeigt den LoginScreen und wartet auf Login, Registrierung oder Dev-Mode. */
function showLoginScreen(): Promise<AuthUser | null> {
  return new Promise((resolve) => {
    const screen = new LoginScreen();
    screen.show();

    screen.onLogin(async (accountName, password) => {
      screen.setLoading(true);
      screen.showError('');
      const result = await authService.signIn(accountName, password);
      if (result.error) {
        if (result.errorCode === 'EMAIL_NOT_CONFIRMED') {
          screen.showConfirmation(result.error);
        } else {
          screen.showError(result.error);
        }
        screen.setLoading(false);
        return;
      }
      screen.hide();
      resolve(result.user);
    });

    screen.onRegister(async (accountName, email, password) => {
      screen.setLoading(true);
      screen.showError('');
      const result = await authService.signUp(accountName, email, password);
      if (result.error) {
        screen.showError(result.error);
        screen.setLoading(false);
        return;
      }
      screen.showConfirmation(
        'Bestätigungs-Mail gesendet — prüfe dein Postfach und melde dich dann hier an',
      );
      screen.setLoading(false);
      screen.switchToLogin();
    });

    screen.onDevMode((code) => {
      if (code === 'unlimiteduniverse') {
        authService.enterDevMode();
        screen.hide();
        resolve(null);
      }
    });
  });
}

/** Zeigt den Weltenauswahl-Screen. */
function showWorldSelect(playerId: string): Promise<string> {
  return new Promise((resolve) => {
    const screen = new WorldSelectScreen();

    (async () => {
      const [worlds, playerWorlds] = await Promise.all([
        getAvailableWorlds(supabase),
        getPlayerWorlds(supabase, playerId),
      ]);
      screen.setJoinedWorlds(playerWorlds.map((w) => w.worldId));
      screen.setWorlds(worlds);
      screen.show();
    })();

    screen.onWorldSelected(async (worldId) => {
      screen.setLoading(true);
      screen.showError('');
      const result = await joinWorld(supabase, worldId);
      if (!result) {
        screen.showError('Beitritt fehlgeschlagen');
        screen.setLoading(false);
        return;
      }
      screen.hide();
      resolve(worldId);
    });
  });
}

async function main(): Promise<void> {
  // 1. Auth prüfen
  const session = await authService.checkSession();
  let user: AuthUser | null = session.user;
  let isDevMode = false;

  if (!user) {
    user = await showLoginScreen();
    if (!user) isDevMode = true;
  }

  // 2. Welt bestimmen
  let worldId: string | null = null;
  let playerId = '';
  let startPlanet: PlanetSummary | null = null;

  if (user && !isDevMode) {
    playerId = user.playerId;

    // Letzte Welt prüfen
    const lastWorldId = await getLastWorldId(supabase, playerId);
    if (lastWorldId) {
      // Prüfe ob Welt noch existiert
      const { data: worldCheck } = await supabase
        .from('worlds')
        .select('id')
        .eq('id', lastWorldId)
        .maybeSingle();
      if (worldCheck) {
        worldId = lastWorldId;
        // join-world aufrufen um last_world_id zu aktualisieren
        const result = await joinWorld(supabase, worldId);
        if (result?.planet) startPlanet = result.planet;
      }
    }

    if (!worldId) {
      worldId = await showWorldSelect(playerId);
      const planets = await getPlayerPlanets(supabase, playerId, worldId);
      startPlanet = planets[0] ?? null;
    }

    // Falls kein startPlanet, nochmal laden (könnte bei join-world gesetzt worden sein)
    if (!startPlanet) {
      const planets = await getPlayerPlanets(supabase, playerId, worldId);
      startPlanet = planets[0] ?? null;
    }
  }

  // 3. LoadingScreen starten
  const loading = new LoadingScreen();
  loading.show();
  loading.setProgress(10, 'engine...');

  const onLogout = isDevMode
    ? null
    : () => { authService.signOut().then(() => location.reload()); };

  await bootGame(supabase, loading, isDevMode ? null : user, onLogout, startPlanet);
}

main().catch((err) => {
  console.error('[MAIN] Fatal:', err);
  const loading = document.getElementById('loading-screen');
  if (loading) {
    loading.style.display = 'flex';
    const status = document.getElementById('loading-status');
    const bar = document.getElementById('loading-bar');
    if (status) { status.textContent = 'server nicht erreichbar'; status.style.color = '#f88'; }
    if (bar) { bar.style.background = '#f44'; bar.style.width = '100%'; }
  }
});
